// ============================================================================
// LOGO UPLOADER
// Componente reutilizable para subir, previsualizar y eliminar logos.
// Usado en CompanyAdminPage (logo empresa) y SitesAdminPage (logo centro).
// ============================================================================

import { useState, useRef } from 'react';
import { Upload, X, Image, Loader2 } from 'lucide-react';
import { uploadLogo, deleteLogo, getLogoUrl } from '../../api/upload';

interface LogoUploaderProps {
  /** URL relativa actual del logo (guardada en BD). Null si no hay logo. */
  currentLogoUrl: string | null | undefined;
  /** Label descriptivo que se muestra sobre el componente */
  label: string;
  /** Texto de ayuda adicional (ej: "Aparece en la esquina izquierda del PDF") */
  hint?: string;
  /** Callback cuando el logo cambia (URL relativa nueva o null si se elimina) */
  onChange: (url: string | null) => void;
  /** Si es false deshabilita el componente */
  disabled?: boolean;
}

const MAX_SIZE_MB = 2;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

export function LogoUploader({
  currentLogoUrl,
  label,
  hint,
  onChange,
  disabled = false,
}: LogoUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging]   = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl]   = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // URL para mostrar: primero el preview local, luego la URL del backend
  const displayUrl = previewUrl || getLogoUrl(currentLogoUrl);

  const handleFile = async (file: File) => {
    setUploadError(null);

    // Validaciones cliente
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Formato no permitido. Usa JPG, PNG, GIF, WebP o SVG.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadError(`El fichero no puede superar ${MAX_SIZE_MB} MB.`);
      return;
    }

    // Preview local inmediato
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    setIsUploading(true);
    try {
      const relativeUrl = await uploadLogo(file);
      onChange(relativeUrl);
      // Ya no necesitamos el preview local — usamos la URL del backend
      URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
    } catch (err: any) {
      setUploadError(err.message || 'Error al subir el logo.');
      URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Resetear el input para permitir volver a subir el mismo fichero
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = async () => {
    if (disabled || isUploading) return;
    if (currentLogoUrl) {
      try {
        await deleteLogo(currentLogoUrl);
      } catch {
        // Si el borrado falla en servidor no bloqueamos la UI
      }
    }
    setPreviewUrl(null);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      {/* Label */}
      <div>
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      </div>

      {/* Zona de upload / preview */}
      {displayUrl ? (
        /* ── Preview del logo actual ──────────────────────────────────────── */
        <div className="flex items-start gap-4">
          <div className="relative w-32 h-20 bg-gray-50 border border-gray-200 rounded-xl overflow-hidden flex items-center justify-center">
            <img
              src={displayUrl}
              alt="Logo"
              className="max-w-full max-h-full object-contain p-2"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || isUploading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <Upload className="h-3.5 w-3.5" />
              Cambiar logo
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || isUploading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Eliminar logo
            </button>
          </div>
        </div>
      ) : (
        /* ── Zona de drop (sin logo) ──────────────────────────────────────── */
        <div
          onClick={() => !disabled && !isUploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`
            relative flex flex-col items-center justify-center gap-2
            w-full h-28 border-2 border-dashed rounded-xl cursor-pointer
            transition-colors
            ${isDragging
              ? 'border-primary bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-primary hover:bg-blue-50/30'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-xs text-gray-500">Subiendo...</span>
            </>
          ) : (
            <>
              <div className="p-2 bg-white rounded-lg border border-gray-200 shadow-sm">
                <Image className="h-5 w-5 text-gray-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">
                  Arrastra una imagen o{' '}
                  <span className="text-primary underline">selecciona un fichero</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  JPG, PNG, WebP o SVG · Máx. {MAX_SIZE_MB} MB
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Error de upload */}
      {uploadError && (
        <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
          <X className="h-4 w-4 text-red-500 shrink-0" />
          <p className="text-xs text-red-600">{uploadError}</p>
        </div>
      )}

      {/* Input oculto */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled || isUploading}
      />
    </div>
  );
}
